import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  CardActions,
  Button,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  CircularProgress
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { API_URL } from '../config';

const plans = [
  {
    id: 'free',
    name: 'Free',
    price: '0',
    interval: 'month',
    features: [
      '10 Resume Analyses per Month',
      'Resume Analysis',
      'Enhanced Resume Generation',
      'Cover Letter Generation',
      'Basic Support'
    ],
    priceId: null
  },
  {
    id: 'basic',
    name: 'Basic',
    price: '4.99',
    interval: 'month',
    features: [
      '200 Resume Analyses per Month',
      'All Free Features',
      'Priority Processing',
      'Email Support',
      'PDF Export'
    ],
    priceId: process.env.REACT_APP_STRIPE_PRICE_BASIC
  },
  {
    id: 'pro',
    name: 'Professional',
    price: '9.99',
    interval: 'month',
    features: [
      '500 Resume Analyses per Month',
      'All Basic Features',
      'Advanced Analytics',
      'Chat Support',
      'Bulk Processing'
    ],
    popular: true,
    priceId: process.env.REACT_APP_STRIPE_PRICE_PRO
  }
];

function Pricing() {
  const navigate = useNavigate();
  const { token, isAuthenticated } = useAuth();
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isAuthenticated) {
      fetchSubscription();
    } else {
      setLoading(false);
    }
  }, [isAuthenticated, token]);

  const fetchSubscription = async () => {
    try {
      const response = await axios.get(`${API_URL}/subscription`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      setSubscription(response.data.subscription);
    } catch (err) {
      console.error('Error fetching subscription:', err);
    } finally {
      setLoading(false);
    }
  };

  const API_URL = process.env.REACT_APP_API_URL;

  const handleSubscribe = async (priceId) => {
    if (priceId === null) {
      // For free plan, just redirect to image generator if logged in
      if (!isAuthenticated) {
        window.location.href = '/signin?redirect=/image-generator';
        return;
      }
      window.location.href = '/image-generator';
      return;
    }

    if (!isAuthenticated) {
      window.location.href = '/signin?redirect=/pricing';
      return;
    }

    try {
      setLoading(true);
      const response = await axios.post(
        `${API_URL}/create-checkout-session`,
        { priceId },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      
      window.location.href = response.data.url;
    } catch (error) {
      console.error('Error creating checkout session:', error);
      alert('Failed to start checkout process. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getButtonText = (plan) => {
    if (loading) return <CircularProgress size={24} sx={{ color: '#000' }} />;
    if (plan.id === 'free') return 'Start Free';
    return 'Subscribe Now';
  };

  const getButtonStyles = (plan) => ({
    mt: 2,
    py: 1.5,
    bgcolor: '#00ffea',
    color: '#000',
    border: 'none',
    fontWeight: 600,
    borderRadius: 2,
    '&:hover': {
      bgcolor: '#00b8ff',
      transform: 'scale(1.02)',
      border: 'none',
      color: '#000'
    },
    '&.Mui-disabled': {
      background: 'rgba(0, 255, 234, 0.3)',
      color: '#000',
      opacity: 0.9,
      border: '1px solid rgba(0, 255, 234, 0.5)'
    },
    transition: 'all 0.2s ease-in-out',
    ...(plan.id === 'free' && {
      bgcolor: '#00ffea',
      '&:hover': {
        bgcolor: '#00b8ff',
        transform: 'scale(1.02)'
      }
    })
  });

  const renderPlanButton = (plan) => {
    if (!subscription || subscription.status === 'cancelled') {
      return (
        <Button
          variant="contained"
          size="large"
          fullWidth
          onClick={() => handleSubscribe(plan.priceId)}
          sx={getButtonStyles(plan)}
        >
          {getButtonText(plan)}
        </Button>
      );
    }
    
    // If user has an active subscription
    if (subscription.status === 'active') {
      if (subscription.stripePriceId === plan.priceId) {
        return (
          <Button
            variant="outlined"
            size="large"
            fullWidth
            disabled
            sx={{
              mt: 2,
              py: 1.5,
              borderColor: '#00ffea',
              color: '#00ffea',
              fontWeight: 600,
              borderRadius: 2
            }}
          >
            Current Plan
          </Button>
        );
      } else {
        return (
          <Button
            variant="contained"
            size="large"
            fullWidth
            onClick={() => navigate('/profile')}
            sx={getButtonStyles(plan)}
          >
            Change Plan
          </Button>
        );
      }
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 8 }}>
      {subscription?.status === 'active' && (
        <Box sx={{ mb: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="#00ffea" gutterBottom>
            You're Currently Subscribed
          </Typography>
          <Typography variant="body1" color="rgba(255, 255, 255, 0.7)">
            To change your plan, please visit your profile page
          </Typography>
        </Box>
      )}

      <Box textAlign="center" mb={8}>
        <Typography
          variant="h2"
          component="h1"
          gutterBottom
          sx={{
            fontWeight: 700,
            color: '#00ffea'
          }}
        >
          Choose Your Plan
        </Typography>
        <Typography
          variant="h5"
          color="rgba(255, 255, 255, 0.7)"
          sx={{ maxWidth: '800px', mx: 'auto', mb: 4 }}
        >
          Unlock more analyses and features with our premium plans
        </Typography>
      </Box>

      <Grid container spacing={4} justifyContent="center" sx={{ px: { xs: 2, md: 0 } }}>
        {plans.map((plan) => (
          <Grid item xs={12} sm={6} md={3} key={plan.id}>
            <Card
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                position: 'relative',
                overflow: 'visible',
                backgroundColor: '#141519',
                border: plan.id === (subscription?.plan || 'free') ? '2px solid #00ffea' : '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: 3,
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: '0 8px 24px rgba(0, 255, 234, 0.15)'
                }
              }}
            >
              {plan.popular && (
                <Chip
                  label="Most Popular"
                  color="primary"
                  size="small"
                  sx={{
                    position: 'absolute',
                    top: -12,
                    right: 24,
                    backgroundColor: '#00ffea',
                    color: '#000',
                    fontWeight: 600,
                    boxShadow: '0 4px 8px rgba(0, 255, 234, 0.3)'
                  }}
                />
              )}
              <CardContent sx={{ p: 3, flexGrow: 1 }}>
                <Typography variant="h5" component="div" sx={{ 
                  fontWeight: 600, 
                  color: '#fff',
                  textAlign: 'center',
                  mb: 2
                }}>
                  {plan.name}
                </Typography>
                
                <Box sx={{ 
                  textAlign: 'center',
                  py: 2,
                  background: 'linear-gradient(135deg, rgba(0, 255, 234, 0.1), rgba(0, 184, 255, 0.1))',
                  borderRadius: 2,
                  mb: 3
                }}>
                  <Typography variant="h3" sx={{ 
                    color: '#00ffea',
                    fontWeight: 700,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 0.5
                  }}>
                    {plan.price === '0' ? 'Free' : (
                      <>
                        <span style={{ fontSize: '1.5rem' }}>$</span>
                        {plan.price}
                      </>
                    )}
                  </Typography>
                  <Typography variant="subtitle2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    per {plan.interval}
                  </Typography>
                </Box>

                <List dense>
                  {plan.features.map((feature, index) => (
                    <ListItem key={index} sx={{ py: 1, px: 0 }}>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <CheckIcon sx={{ 
                          color: index === 0 ? '#00ffea' : 'rgba(0, 255, 234, 0.5)',
                          fontSize: index === 0 ? 24 : 20
                        }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary={feature} 
                        sx={{ 
                          '& .MuiListItemText-primary': { 
                            color: index === 0 ? '#fff' : 'rgba(255, 255, 255, 0.7)',
                            fontWeight: index === 0 ? 600 : 400,
                            fontSize: index === 0 ? '1rem' : '0.875rem'
                          }
                        }}
                      />
                    </ListItem>
                  ))}
                </List>
              </CardContent>

              <CardActions sx={{ p: 3, pt: 0 }}>
                {renderPlanButton(plan)}
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Container>
  );
}

export default Pricing; 